class LSTeamSection extends HTMLElement {
  constructor() {
    super();
    this.imageItems = this.querySelectorAll('.ls-team-image-item');

    // Only initialize if there are images
    if (this.imageItems.length === 0) return;

    // Create or find containers dynamically
    this.setupContainers();

    if (window.innerWidth <= 749) {
      this.initMobileCarousel();
    } else {
      this.initDesktopHover();
    }
  }

  setupContainers() {
    // Check if images wrapper exists, if not create it
    let imagesWrapper = this.querySelector('.ls-team-images-wrapper');

    if (!imagesWrapper) {
      // Create wrapper structure
      imagesWrapper = document.createElement('div');
      imagesWrapper.classList.add('ls-team-images-wrapper');

      const imagesContainer = document.createElement('div');
      imagesContainer.classList.add('ls-team-images');

      const dotsContainer = document.createElement('div');
      dotsContainer.classList.add('ls-team-dots');

      // Create progress bar for mobile
      const progressBar = document.createElement('div');
      progressBar.classList.add('ls-team-progress-bar');

      const progressTrack = document.createElement('div');
      progressTrack.classList.add('ls-team-progress-track');

      const progressFill = document.createElement('div');
      progressFill.classList.add('ls-team-progress-fill');

      progressTrack.appendChild(progressFill);
      progressBar.appendChild(progressTrack);

      // Move all image items to the images container
      this.imageItems.forEach(item => {
        imagesContainer.appendChild(item);
      });

      imagesWrapper.appendChild(imagesContainer);
      imagesWrapper.appendChild(dotsContainer);
      imagesWrapper.appendChild(progressBar);

      // Append to the main container
      const mainContainer = this.querySelector('.ls-team-container');
      if (mainContainer) {
        mainContainer.appendChild(imagesWrapper);
      }
    }

    this.imagesContainer = this.querySelector('.ls-team-images');
    this.dotsContainer = this.querySelector('.ls-team-dots');
    this.progressFill = this.querySelector('.ls-team-progress-fill');

    // Re-query image items in case they were moved
    this.imageItems = this.querySelectorAll('.ls-team-image-item');
  }

  initDesktopHover() {
    // Set first image as active by default
    if (this.imageItems.length > 0) {
      this.imageItems[0].classList.add('active');
    }

    // Add hover and keyboard listeners to all images
    this.imageItems.forEach((item) => {
      // Mouse hover
      item.addEventListener('mouseenter', () => {
        this.setActiveImage(Array.from(this.imageItems).indexOf(item));
      });

      // Keyboard interaction
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.setActiveImage(Array.from(this.imageItems).indexOf(item));
        }
      });

      // Focus handling
      item.addEventListener('focus', () => {
        this.setActiveImage(Array.from(this.imageItems).indexOf(item));
      });
    });
  }

  initMobileCarousel() {
    // Set first image as active by default
    if (this.imageItems.length > 0) {
      this.imageItems[0].classList.add('active');
    }

    // Create dots only if container exists
    if (this.dotsContainer) {
      this.dotsContainer.innerHTML = '';
      this.imageItems.forEach((item, index) => {
        const dot = document.createElement('div');
        dot.classList.add('ls-team-dot');
        if (index === 0) dot.classList.add('active');
        dot.addEventListener('click', () => {
          this.scrollToImage(index);
          this.setActiveImage(index);
        });
        this.dotsContainer.appendChild(dot);
      });
    }

    // Add keyboard navigation for mobile
    this.imageItems.forEach((item, index) => {
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.scrollToImage(index);
          this.setActiveImage(index);
        } else if (e.key === 'ArrowLeft' && index > 0) {
          e.preventDefault();
          this.scrollToImage(index - 1);
          this.imageItems[index - 1].focus();
        } else if (e.key === 'ArrowRight' && index < this.imageItems.length - 1) {
          e.preventDefault();
          this.scrollToImage(index + 1);
          this.imageItems[index + 1].focus();
        }
      });
    });

    // Update active dot, image, and progress bar on scroll
    if (this.imagesContainer) {
      this.imagesContainer.addEventListener('scroll', () => {
        this.updateActiveDot();
        this.updateActiveImageOnScroll();
        this.updateProgressBar();
      });
    }

    // Initialize progress bar
    this.updateProgressBar();
  }

  updateProgressBar() {
    if (!this.progressFill || !this.imagesContainer || this.imageItems.length === 0) return;

    const scrollLeft = this.imagesContainer.scrollLeft;
    const scrollWidth = this.imagesContainer.scrollWidth - this.imagesContainer.offsetWidth;
    const scrollPercentage = scrollWidth > 0 ? scrollLeft / scrollWidth : 0;

    // Calculate the translate distance for the progress fill
    const trackWidth = this.progressFill.parentElement.offsetWidth;
    const fillWidth = this.progressFill.offsetWidth; // Get actual width from CSS (4.75rem)
    const maxTranslate = trackWidth - fillWidth;
    const translateX = scrollPercentage * maxTranslate;

    this.progressFill.style.transform = `translateX(${translateX}px)`;
  }

  setActiveImage(index) {
    // Remove active class from all images
    this.imageItems.forEach((img) => img.classList.remove('active'));
    // Add active class to specific image
    if (this.imageItems[index]) {
      this.imageItems[index].classList.add('active');
    }
  }

  updateActiveImageOnScroll() {
    if (!this.imagesContainer || this.imageItems.length === 0) return;

    const scrollLeft = this.imagesContainer.scrollLeft;
    const containerWidth = this.imagesContainer.offsetWidth;
    const scrollCenter = scrollLeft + containerWidth / 2;
    const scrollWidth = this.imagesContainer.scrollWidth;

    // Check if we're at the very beginning
    if (scrollLeft <= 10) {
      this.setActiveImage(0);
      return;
    }

    // Check if we're at the very end
    if (scrollLeft + containerWidth >= scrollWidth - 10) {
      this.setActiveImage(this.imageItems.length - 1);
      return;
    }

    // Find which image is closest to the center
    let closestIndex = 0;
    let closestDistance = Infinity;

    this.imageItems.forEach((item, index) => {
      const itemLeft = item.offsetLeft;
      const itemCenter = itemLeft + item.offsetWidth / 2;
      const distance = Math.abs(scrollCenter - itemCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.setActiveImage(closestIndex);
  }

  scrollToImage(index) {
    const imageItem = this.imageItems[index];
    if (imageItem) {
      imageItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }

  updateActiveDot() {
    if (!this.imagesContainer || !this.dotsContainer || this.imageItems.length === 0) return;

    const scrollLeft = this.imagesContainer.scrollLeft;
    const itemWidth = this.imageItems[0].offsetWidth + 16; // width + gap
    const activeIndex = Math.round(scrollLeft / itemWidth);

    const dots = this.dotsContainer.querySelectorAll('.ls-team-dot');
    dots.forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });
  }
}

customElements.define('ls-team-section', LSTeamSection);
